import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    Divider,
    Alert,
    Chip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useToken } from '../items/UseToken';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { ApiEndpointsReadyState } from '../ReadyStates';



const routeRegex = /^\/[a-z0-9\-\/]*$/;

// =====================
// helpers
// =====================
function decodeBase64Utf8(b64) {
    try {
        return decodeURIComponent(
            Array.prototype.map.call(
                atob(b64),
                c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            ).join('')
        );
    } catch {
        try { return atob(b64); } catch { return ''; }
    }
}

function extractInputsFromInstructionBase64(instructionB64) {
    if (!instructionB64) return [];

    try {
        const json = decodeBase64Utf8(instructionB64);
        const root = JSON.parse(json);
        const vars = root?.variables;

        if (!Array.isArray(vars)) return [];

        return vars
            .filter(v => v?.name && (v.aiInput === 'required' || v.aiInput === 'optional'))
            .map(v => ({ key: v.name, value: v.value ?? '' }));
    } catch {
        return [];
    }
}

// =====================
// component
// =====================
export default function ApiEndpointsTab({ emptyState }) {
    const { token } = useToken();

    // data
    const [processes, setProcesses] = useState([]);
    const [machines, setMachines] = useState([]);
    const [endpoints, setEndpoints] = useState([]);
    const [deleteOpen, setDeleteOpen] = useState(false);


    // mode
    const [selectedEndpoint, setSelectedEndpoint] = useState(null);
    const [isCreatingNew, setIsCreatingNew] = useState(false);

    // form (only for create)
    const [name, setName] = useState('');
    const [route, setRoute] = useState('');
    const [processGuid, setProcessGuid] = useState('');
    const [machineGuid, setMachineGuid] = useState('');
    const [description, setDescription] = useState('');

    // derived
    const [inputs, setInputs] = useState([]);

    // feedback
    const [createdEndpoint, setCreatedEndpoint] = useState(null);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const handleDelete = () => {
        if (!selectedEndpoint) return;

        fetch(`api/endpoints/${selectedEndpoint.guid}`, {
            method: 'DELETE',
            headers: {
                Authorization: 'Bearer ' + token
            }
        })
            .then(() => {
                setDeleteOpen(false);
                setSelectedEndpoint(null);
                setRoute('');
                setProcessGuid('');
                setMachineGuid('');
                setInputs([]);
                refreshEndpoints();
            });
    };


    // =====================
    // load data
    // =====================
    const refreshEndpoints = () => {
        fetch('api/endpoints', {
            headers: { Authorization: 'Bearer ' + token }
        })
            .then(r => r.json())
            .then(r => setEndpoints(r.results || []))
            .catch(() => setEndpoints([]));
    };

    useEffect(() => {
        fetch('api/getrobots?is_active=1', {
            headers: { Authorization: 'Bearer ' + token }
        })
            .then(r => r.json())
            .then(r => setProcesses(r.results || []));

        fetch('api/getmachines', {
            headers: { Authorization: 'Bearer ' + token }
        })
            .then(r => r.json())
            .then(r => setMachines(r.results || []));

        refreshEndpoints();
    }, [token]);

    // =====================
    // inputs from process
    // =====================
    useEffect(() => {
        if (!processGuid) {
            setInputs([]);
            return;
        }

        const proc = processes.find(p => p.guid === processGuid);
        setInputs(extractInputsFromInstructionBase64(proc?.instruction));
    }, [processGuid, processes]);

    // =====================
    // validation
    // =====================
    const isRouteValid = routeRegex.test(route);
    const isFormValid =
        isCreatingNew &&
        name.length >= 3 &&
        route &&
        isRouteValid &&
        processGuid &&
        machineGuid;

    // =====================
    // preview helpers
    // =====================
    const fullUrl = useMemo(() => {
        return route ? `${window.location.origin}/api/endpoint${route}` : '';
    }, [route]);

    const previewApiKey =
        selectedEndpoint?.api_key ||
        createdEndpoint?.apiKey ||
        'YOUR_API_KEY';

    const exampleBody = useMemo(() => {
        return JSON.stringify(
            Object.fromEntries(inputs.map(i => [i.key, '...'])),
            null,
            2
        );
    }, [inputs]);

    const exampleCurl = useMemo(() => {
        if (!route) return '';
        return `curl -X POST "${fullUrl}"
-H "X-API-Key: ${previewApiKey}"
-H "Content-Type: application/json"
-d ${exampleBody}`;
    }, [fullUrl, previewApiKey, exampleBody, route]);

    // =====================
    // actions
    // =====================
    const handleSelectEndpoint = (ep) => {
        setSelectedEndpoint(ep);
        setIsCreatingNew(false);
        setCreatedEndpoint(null);
        setError('');
        setSuccessMsg('');

        setRoute(ep.route);
        setProcessGuid(ep.process_guid);
        setMachineGuid(ep.machine_guid);
    };

    const handleNewEndpoint = () => {
        setIsCreatingNew(true);
        setSelectedEndpoint(null);
        setCreatedEndpoint(null);
        setError('');
        setSuccessMsg('');

        setName('');
        setRoute('');
        setProcessGuid('');
        setMachineGuid('');
        setDescription('');
        setInputs([]);
    };

    const handleCreate = () => {
        setError('');
        setSuccessMsg('');

        fetch('api/endpoints', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            body: JSON.stringify({
                name,
                route,
                process_guid: processGuid,
                machine: machines.find(m => m.guid === machineGuid)?.name,
                machine_guid: machineGuid,
                description
            })
        })
            .then(async r => {
                if (!r.ok) throw new Error(await r.text());
                return r.json();
            })
            .then(res => {
                setCreatedEndpoint(res);
                setSuccessMsg('Endpoint created.');
                refreshEndpoints();
            })
            .catch(err => setError(err.message || 'Create failed'));
    };

    const showEmpty = endpoints.length === 0;

    const copy = (text) => navigator.clipboard.writeText(text);

    // =====================
    // render
    // =====================
    return (
        <>
            {showEmpty ? (<Box sx={{ p: 4 }}>
                {emptyState}
            </Box>) :
                <ApiEndpointsReadyState/>
        }
        <Grid container spacing={2}>
            
            {/* ===== LIST ===== */}
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">API Endpoints</Typography>
                            <Button variant="contained" onClick={handleNewEndpoint}>
                                New Endpoint
                            </Button>
                        </Box>

                        <Table size="small" sx={{ mt: 2 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Route</TableCell>
                                    <TableCell>Machine</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {endpoints.map(e => (
                                    <TableRow
                                        key={e.guid}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleSelectEndpoint(e)}
                                    >
                                        <TableCell>{e.name}</TableCell>
                                        <TableCell>
                                            <code>{`/api/endpoint${e.route}`}</code>
                                        </TableCell>
                                        <TableCell>{e.machine}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={String(e.is_active) === '1' ? 'Active' : 'Disabled'}
                                                color={String(e.is_active) === '1' ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </Grid>

            {/* ===== CREATE ===== */}
            {isCreatingNew && (
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6">Create New Endpoint</Typography>

                            {error && <Alert severity="error">{error}</Alert>}
                            {successMsg && <Alert severity="success">{successMsg}</Alert>}

                            <TextField fullWidth label="Name" value={name}
                                onChange={e => setName(e.target.value)} sx={{ mt: 2 }} />

                            <TextField fullWidth label="Route" value={route}
                                error={!!route && !isRouteValid}
                                onChange={e => {
                                    let val = e.target.value;

                                    // usu� spacje
                                    val = val.replace(/\s+/g, '');

                                    // wymu� leading /
                                    if (val && !val.startsWith('/')) {
                                        val = '/' + val;
                                    }

                                    setRoute(val);}} sx={{ mt: 2 }} />

                            <TextField select fullWidth label="Process"
                                value={processGuid} SelectProps={{ native: true }}
                                onChange={e => setProcessGuid(e.target.value)} sx={{ mt: 2 }}>
                                <option value="" />
                                {processes.map(p => (
                                    <option key={p.guid} value={p.guid}>
                                        {p.name} v{p.version}
                                    </option>
                                ))}
                            </TextField>

                            <TextField select fullWidth label="Machine"
                                value={machineGuid} SelectProps={{ native: true }}
                                onChange={e => setMachineGuid(e.target.value)} sx={{ mt: 2 }}>
                                <option value="" />
                                {machines.map(m => (
                                    <option key={m.guid} value={m.guid}>
                                        {m.name}
                                    </option>
                                ))}
                            </TextField>

                            <TextField fullWidth multiline rows={2}
                                label="Description"
                                value={description}
                                onChange={e => setDescription(e.target.value)} sx={{ mt: 2 }} />

                            <Box mt={2} textAlign="right">
                                <Button variant="contained" disabled={!isFormValid} onClick={handleCreate}>
                                    Create
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            )}

            {/* ===== PREVIEW ===== */}
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">
                                {selectedEndpoint
                                    ? `Endpoint Preview: ${selectedEndpoint.name}`
                                    : isCreatingNew
                                        ? 'New Endpoint Preview'
                                        : 'Select an endpoint'}
                            </Typography>

                            {selectedEndpoint && (
                                <Tooltip title="Delete endpoint">
                                    <IconButton color="error" onClick={() => setDeleteOpen(true)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>


                        {route && (
                            <>
                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2">POST {fullUrl}</Typography>

                                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                                    Required Inputs
                                </Typography>

                                {inputs.length === 0
                                    ? <Typography>No inputs</Typography>
                                    : inputs.map(i => (
                                        <Chip key={i.key} label={i.key} size="small" sx={{ mr: 1, mt: 1 }} />
                                    ))}

                                <Divider sx={{ my: 2 }} />

                                <Typography
                                    variant="subtitle2"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    Example curl
                                    <Tooltip title="Copy curl">
                                        <IconButton size="small" onClick={() => copy(exampleCurl)}>
                                            <ContentCopyIcon fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                </Typography>

                                <Box sx={{
                                    mt: 1,
                                    fontFamily: 'monospace',
                                    bgcolor: '#111',
                                    color: '#0f0',
                                    p: 1,
                                    borderRadius: 1,
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {exampleCurl}
                                </Box>

                                {selectedEndpoint?.api_key && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        API Key (read-only): <b>{selectedEndpoint.api_key}</b>
                                    </Alert>
                                )}

                                {createdEndpoint?.apiKey && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        Save this API key now:<br />
                                        <b>{createdEndpoint.apiKey}</b>
                                    </Alert>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
                <DialogTitle>Delete API Endpoint</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete endpoint{' '}
                        <b>{selectedEndpoint?.name}</b>?
                    </Typography>
                    <Typography color="error" sx={{ mt: 1 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            </Grid>
        </>

    );
}
